/**
 * A hand-made dungeon used until the Build screen writes one: a way in, a junction,
 * a room and a trap run off it, and the objective behind a checkpoint.
 *
 * Every block goes on through `placeBlock`, and every origin comes out of
 * `matingOrigin` rather than being written down, so the sample cannot ship a board
 * §4.2's rules would refuse and a block whose ports move is left out rather than
 * drawn somewhere wrong. It is a placeholder for the render path, not a design.
 */
import {
  createEmptyLevel,
  matingOrigin,
  orientBlock,
  placeBlock,
  resolvePlacement,
  type BlockCatalogue,
  type BlockId,
  type BlockRotation,
  type Cell,
  type DungeonLevel,
  type PlacedBlock,
} from '@dungeon-builder/shared';

/** The block a dungeon is entered through (§5.5), and so where its players stand. */
export const ENTRANCE_BLOCK = 'goal.entrance';

/** Where a block already on the board is to be joined: which instance, which port. */
interface Mate {
  instanceId: string;
  portIndex: number;
}

export function createSampleLevel(catalogue: BlockCatalogue): DungeonLevel {
  const blocks: PlacedBlock[] = [];

  /** Puts a block on the board if the three rules take it, and says so if not. */
  function drop(instanceId: string, block: BlockId, at: Cell, rotation: BlockRotation): void {
    const candidate: PlacedBlock = { instanceId, block, at, rotation };
    const result = placeBlock(blocks, candidate, catalogue);
    if (!result.accepted) {
      console.warn(`[sample] ${instanceId} left out: ${result.reason}`);
      return;
    }
    blocks.push(candidate);
  }

  /**
   * Joins `block`, turned, to a port of something already placed, by mating its own
   * `portIndex` with that one. A missing block or an unreachable mate is a catalogue
   * the sample was not authored against: it loses this block and whatever hangs off
   * it, which is the honest thing to draw.
   */
  function attach(
    instanceId: string,
    block: BlockId,
    portIndex: number,
    to: Mate,
    rotation: BlockRotation = 0,
  ): void {
    const placed = blocks.find((candidate) => candidate.instanceId === to.instanceId);
    const open = placed && resolvePlacement(placed, catalogue)?.ports[to.portIndex];
    const definition = catalogue[block];
    if (!open || !definition) {
      console.warn(`[sample] ${instanceId} left out: nothing to join at ${to.instanceId}.`);
      return;
    }
    const at = matingOrigin(orientBlock(definition, rotation), portIndex, open);
    if (!at) {
      console.warn(`[sample] ${instanceId} left out: its port ${portIndex} cannot mate that one.`);
      return;
    }
    drop(instanceId, block, at, rotation);
  }

  // The way in, and the corridor south out of it -- the entrance's only port.
  drop('blk_entrance', ENTRANCE_BLOCK, { x: 0, z: 0 }, 0);
  attach('blk_corridor', 'link.corridor', 0, { instanceId: 'blk_entrance', portIndex: 0 });

  // The junction the dungeon branches at: four ports, one spent on the way in.
  attach('blk_cross', 'link.cross', 0, { instanceId: 'blk_corridor', portIndex: 1 });

  // Straight on: the checkpoint, and the objective behind it.
  attach('blk_door', 'link.door', 0, { instanceId: 'blk_cross', portIndex: 2 });
  attach('blk_vault', 'goal.vault', 0, { instanceId: 'blk_door', portIndex: 1 });

  // East off the junction a room, reached through its west port; west the trap run,
  // turned a quarter so it runs away from the junction rather than across it.
  attach('blk_armory', 'room.armory', 1, { instanceId: 'blk_cross', portIndex: 1 });
  attach('blk_gauntlet', 'threat.gauntlet', 0, { instanceId: 'blk_cross', portIndex: 3 }, 90);

  return createEmptyLevel({ name: 'Placeholder Crypt', blocks });
}
